import axios from "axios";
import { get, isEmpty, map, maxBy } from "lodash";
import omitDeep from "omit-deep-lodash";
import { useContext, useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import { useSearchParams } from "react-router-dom";
import Loading from "shared/components/Spin";
import { useLazyQuery } from "shared/hooks/useApi";
import { useClearSearchParams } from "shared/hooks/useClearSearchParams";
import { StoreContext } from "shared/store";
import graphql from "utils/api/graphql";
import { PROJECT } from "utils/api/graphql/queries/projects";
import { TRACERS } from "utils/api/graphql/queries/tracers";
import { SCREENS, TRACER_TYPES } from "utils/constants";

const matchStatusesWithProcess = ({ tracers, statuses }) => {
  const result = map(
    statuses.filter(({ status }) => status),
    ({ status: { id: statusId, order } }) => {
      const allProcessStatuses = tracers.flatMap(
        (tracer) =>
          tracer?.flow?.process?.processStatuses
            ?.filter(({ id }) => id === statusId)
            .map((status) => ({
              ...status,
              tracer,
            })) || [],
      );
      const matchedStatus = maxBy(allProcessStatuses, (candidate) => {
        return -Math.abs(candidate.order - order);
      });

      return {
        tracer: matchedStatus?.tracer || null,
        processStatus: matchedStatus || null,
      };
    },
  ).filter(({ tracer }) => tracer !== null);
  const res = maxBy(result, ({ processStatus: { order } }) =>
    parseInt(order, 10),
  );
  return res;
};

const InitProject = () => {
  const navigate = useNavigate();
  const { externalId } = useParams();
  const [searchParams] = useSearchParams();
  const clearSearchParams = useClearSearchParams();

  const sessionId = searchParams.get("sid");

  const [getProject] = useLazyQuery(PROJECT, {
    variables: { where: { externalId } },
    skip: !externalId,
  });

  const [getTracers] = useLazyQuery(TRACERS, {
    fetchPolicy: "no-cache",
    variables: { where: { type: TRACER_TYPES.MANUAL } },
  });

  const { setUser, setAccessToken, clearStore } = useContext(StoreContext);

  const [checkUser, { loading }] = useLazyQuery(graphql.queries.CHECK_USER, {
    fetchPolicy: "no-cache",
    onCompleted: ({ profile }) => {
      getProject({
        onCompleted: ({ project }) =>
          getTracers({
            onCompleted: ({ tracers: { data } }) => {
              const { tracer } = matchStatusesWithProcess({
                tracers: omitDeep(data, "__typename"),
                statuses: get(project, "statuses", []),
              });
              onSignIn({
                user: profile,
                tracer,
                project,
              });
            },
          }),
      });
    },
    onError: () =>
      navigate(SCREENS.AUTH.NOT_AUTHORIZED.path, { replace: true }),
  });

  useEffect(() => {
    if (externalId && sessionId) {
      axios
        .get(
          `${process.env.REACT_APP_REST_BFF_URL.replace(
            "graphql",
            "",
          )}api/auth/session/resolve/${sessionId}`,
        )
        .then((res) => {
          const { sessionToken } = res.data;
          if (!sessionToken) throw new Error("No session token returned");

          setAccessToken({ accessToken: sessionToken });
          clearSearchParams(["sid"]);
          checkUser();
        })
        .catch(() => {
          clearStore();
          navigate(SCREENS.AUTH.NOT_AUTHORIZED.path);
        });
    }
  }, [externalId, sessionId]);

  const onSignIn = ({ user, project, tracer }) => {
    if (isEmpty(user) || !user.isActive) {
      clearStore();
      return navigate(SCREENS.AUTH.NOT_AUTHORIZED.path);
    }

    setUser({ user });

    navigate(`${SCREENS.PRIVATE.BROKER.PROJECTS.DETAIL.path}/${project.id}`, {
      state: {
        process: get(tracer, "flow.process"),
        tracer,
      },
    });
  };

  if (loading) return <Loading />;
};

export default InitProject;
